import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Bot,
  ChevronLeft,
  Trash2,
  Sparkles,
  Calendar,
  Dumbbell,
  Target,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/styles/ChatBot.css";
import { chatBotApi } from "../services/userService";
const QUICK_ACTIONS = [
  {
    text: "Gym Hours",
    icon: <Dumbbell size={16} />,
    query: "What are the gym hours for today?",
  },
  {
    text: "Book a Court",
    icon: <Target size={16} />,
    query: "How do I book a football court?",
  },
  {
    text: "Latest Activities",
    icon: <Calendar size={16} />,
    query: "Show me the latest activities for my faculty.",
  },
  {
    text: "Points History",
    icon: <Sparkles size={16} />,
    query: "How can I check my points history?",
  },
];
const ChatbotPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  //   const [showDisclaimer, setShowDisclaimer] = useState(true);
  const messagesEndRef = useRef(null);

  const isArabic = (text) => /[\u0600-\u06FF]/.test(text);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  const handleSend = async (text = input) => {
    const trimmedText = text.trim();
    // الـ Validation: منع الإرسال لو بيكتب، أو أقل من حرفين، أو أكتر من 600 حرف
    if (isTyping || trimmedText.length < 2 || trimmedText.length > 600) return;

    const userMsg = {
      id: Date.now(),
      from: "user",
      text: trimmedText,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);

    try {
      const chatHistory = messages.slice(-10).map((m) => ({
        role: m.from === "bot" ? "model" : "user",
        text: m.text,
      }));

      const response = await chatBotApi(trimmedText, chatHistory);

      const botMsg = {
        id: Date.now() + 1,
        from: "bot",
        text: response.data.reply,
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch (error) {
      const errorMsg = {
        id: Date.now() + 1,
        from: "bot",
        text:
          error.message ||
          "System encountered an error connecting to the AI core. Please check your network.",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="chat-page-container">
      <div className="background-mesh"></div>

      <header className="chat-header-premium shadow-sm">
        <div className="container d-flex align-items-center justify-content-between h-100 px-3">
          <div className="d-flex align-items-center gap-3">
            <motion.button
              whileTap={{ scale: 0.9, x: -2 }}
              onClick={() => navigate(-1)}
              className="back-btn-circle border-0 bg-transparent"
            >
              <ChevronLeft size={26} color="white" />
            </motion.button>

            <div className="bot-status-container d-flex align-items-center gap-3">
              <div className="bot-avatar-glow">
                <Bot size={22} color="white" />
                <div className={`pulse-ring ${isTyping ? "active" : ""}`}></div>
              </div>
              <div className="text-container d-none d-sm-block">
                <h6 className="m-0 text-white fw-bold">Campus Pulse AI</h6>
                <div className="d-flex align-items-center gap-2 mt-1">
                  <span className="online-indicator"></span>
                  <span
                    className="status-text text-white-50"
                    style={{ fontSize: "0.75rem", letterSpacing: "1px" }}
                  >
                    {isTyping ? "PROCESSING..." : "ACTIVE NOW"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.1, color: "#ff4d4d" }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setMessages([])}
            className="clear-chat-btn bg-transparent border-0"
            title="Clear Session"
          >
            <Trash2 size={20} color="rgba(255,255,255,0.4)" />
          </motion.button>
        </div>
      </header>

      <main className="chat-messages-area custom-scrollbar">
        <div className="container py-0">
          {/* <AnimatePresence>
            {showDisclaimer && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, y: -20 }}
                className="premium-disclaimer mx-auto mb-5 p-3"
              >
                <div className="d-flex align-items-start gap-3">
                  <div className="icon-wrapper text-info mt-1"><Terminal size={18} /></div>
                  <div className="flex-grow-1">
                    <p className="m-0 small text-white fw-medium">AI Integration Active</p>
                    <p className="m-0 text-white-50" style={{ fontSize: "0.75rem" }}>Providing real-time campus data and activity guidance.</p>
                  </div>
                  <button onClick={() => setShowDisclaimer(false)} className="bg-transparent border-0 text-white-50"><X size={16} /></button>
                </div>
              </motion.div>
            )}
          </AnimatePresence> */}

          {messages.length === 0 && (
            <div className="luxury-empty-state text-center py-5">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 4,
                  ease: "easeInOut",
                }}
                className="mb-4"
              >
                <div className="glow-icon-container">
                  <Sparkles size={50} color="#00d2ff" />
                </div>
              </motion.div>
              <h2 className="text-white fw-bold mb-3">
                How can I help you today?
              </h2>
              <p className="text-white-50 mb-5 px-3 small">
                I'm your intelligent companion for university life, events, and
                bookings.
              </p>

              <div className="quick-action-list d-flex flex-wrap justify-content-center gap-3">
                {QUICK_ACTIONS.map((action, idx) => (
                  <motion.button
                    key={idx}
                    whileHover={{
                      y: -4,
                      borderColor: "rgba(99, 102, 241, 0.5)",
                    }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSend(action.query)}
                    className="premium-quick-btn border-0"
                  >
                    <span className="icon-box shadow-sm">{action.icon}</span>
                    <span className="fw-medium text-white-50 action-text">
                      {action.text}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          <div className="messages-stack d-flex flex-column gap-4 px-2 pb-5">
            <AnimatePresence mode="popLayout">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  layout
                  initial={{ opacity: 0, x: msg.from === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`message-row ${msg.from}`}
                >
                  <div
                    className="message-content-wrapper"
                    style={{ direction: isArabic(msg.text) ? "rtl" : "ltr" }}
                  >
                    <div
                      className={`premium-bubble shadow-lg ${msg.isError ? "bg-danger-subtle text-danger" : ""}`}
                    >
                      <p
                        className="m-0 text-wrap"
                        style={{ lineHeight: "1.6" }}
                      >
                        {msg.text}
                      </p>
                      <div
                        className="d-flex justify-content-between align-items-center mt-2 opacity-50"
                        style={{ fontSize: "0.65rem" }}
                      >
                        <span className="fw-bold text-uppercase">
                          {msg.from === "user" ? "Me" : "Pulse AI"}
                        </span>
                        <span>{msg.time}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isTyping && (
              <div className="message-row bot">
                <div className="ai-skeleton-loader shadow-lg">
                  <div className="skeleton-line full custom-pulse"></div>
                  <div className="skeleton-line half custom-pulse"></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </main>

      <footer className="luxury-input-footer border-0">
        <div className="container">
          <div className="input-group-premium shadow-lg">
            <input
              type="text"
              placeholder={
                isArabic(input)
                  ? "...اسأل Campus Pulse AI"
                  : "Message Campus Pulse AI..."
              }
              className="luxury-input flex-grow-1"
              style={{ direction: isArabic(input) ? "rtl" : "ltr" }}
              value={input}
              maxLength={600}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const trimmed = input.trim();
                  if (trimmed.length >= 2 && trimmed.length <= 600) {
                    handleSend();
                  }
                }
              }}
              autoFocus
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSend()}
              disabled={
                input.trim().length < 2 || input.trim().length > 600 || isTyping
              }
              className={`premium-send-btn ms-2 border-0 ${input.trim().length >= 2 && input.trim().length <= 600 ? "glow-active" : ""}`}
            >
              <Send size={18} />
            </motion.button>
          </div>
        </div>
      </footer>
    </div>
  );
};
export default ChatbotPage;
